// eslint-disable-next-line no-unused-vars
import React, {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import {Card, Pagination} from 'react-bootstrap';
import NewsModal from '../components/ui/modal/newsModal';
import {getNews} from '../api/axios';

// 🟢 News Container
const NewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  max-width: 950px;
  margin: auto;
  border-radius: 10px;

  @media (max-width: 768px) {
    padding: 10px;
    max-width: 100%;
  }
`;

// 🟢 Styled Pagination
const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-bottom: 25px;
  .page-item.active .page-link {
    background-color: #fff;
    color: #000;
  }
  .page-link {
    background-color: #222;
    border: 1px solid #444;
    color: #fff;
  }
  .page-link:hover {
    background-color: #333;
  }
`;

// 🟢 News Card Style
const NewsItem = styled(Card)`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 10px;
  color: #fff;
  padding: 20px;
  border: 1px solid;
  transition: transform 0.3s ease-in-out, background 0.3s;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px;
  }
`;

const NewsImage = styled.img`
  width: 150px;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 20px;
  border: 2px solid #c00000;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    margin-right: 0;
    margin-bottom: 10px;
  }
`;

const NewsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const NewsTitle = styled.h4`
  font-size: 18px;
  color: #ff4444;
  margin-bottom: 6px;
  font-weight: bold;
`;

const NewsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #bbb;
`;

const NewsCategory = styled.span`
  font-weight: bold;
  color: #fff;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FilterButton = styled.button`
  background-color: ${({active}) => (active ? '#c00000' : '#330000')};
  color: ${({active}) => (active ? '#000' : '#fff')};
  width: 150px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }
`;

const Home = () => {
  const {t} = useTranslation();
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [news, setNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [filter, setFilter] = useState('All');
  const pageTitleRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const fetchNews = async () => {
        try {
          const response = await getNews();
          const data = response.data;
          const newsArray = Array.isArray(data) ? data : data.news ? data.news : [];
          setNews(newsArray);
        } catch (error) {
          // Fehlerbehandlung (optional)
        }
      };
      fetchNews();
    }, 1000); // 1000ms Delay

    return () => clearTimeout(timer);
  }, []);

  const filteredNews = filter === 'All' ? news : news.filter(n => n.category === filter);
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const currentNews = filteredNews.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Kategorien (Filter-Buttons). Übersetzungsschlüssel werden in der Übersetzungsdatei hinterlegt.
  const categories = ['All', 'Update', 'Event', 'Maintenance', 'Announcement'];

  return (
    <PageContentComponent>
      <div ref={pageTitleRef}>
        <PageContainerTitleComponent title={t('home.newsTitle')} />
      </div>
      <FilterContainer>
        {categories.map(category => (
          <FilterButton
            key={category}
            active={filter === category}
            onClick={() => {
              setFilter(category);
              setCurrentPage(1);
            }}
          >
            {t(`home.filter.${category.toLowerCase()}`)}
          </FilterButton>
        ))}
      </FilterContainer>
      <NewsContainer>
        {Array.isArray(currentNews) &&
          currentNews.map(newsItem => (
            <NewsItem
              key={newsItem.id}
              onClick={() => {
                setSelectedNews(newsItem);
                setModalShow(true);
              }}
            >
              <NewsImage src={newsItem.image} alt={newsItem.title} />
              <NewsContent>
                <NewsCategory>{newsItem.category}</NewsCategory>
                <NewsTitle>{newsItem.title}</NewsTitle>
                <NewsMeta>
                  <span>{newsItem.dateTime || t('home.noDate')}</span>
                </NewsMeta>
              </NewsContent>
            </NewsItem>
          ))}
      </NewsContainer>
      <StyledPagination>
        {[...Array(totalPages)].map((_, i) => (
          <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
            {i + 1}
          </Pagination.Item>
        ))}
      </StyledPagination>
      {selectedNews && (
        <NewsModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          news={selectedNews}
          allNews={filteredNews}
          setSelectedNews={setSelectedNews}
        />
      )}
    </PageContentComponent>
  );
};

export default Home;
